import { useCallback, useState } from 'react';
import { api } from 'common/build/services/api';
import { backendServices, ILoginResponseViewModel, portalUserService } from 'common';

type SignInState = {
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    error: string | null;
    userId: string | null;
};

/**
 * Same keys the supplier portal stores on login.
 * @see supplier-portal/src/actions/userlogin.js lines 161-168
 */
const AUTH_LOCALSTORAGE_KEYS = [
    'is_logged_in',
    'access_token',
    'refresh_token',
    'token_expires',
    'expires_in',
    'name',
    'company_id',
    'id',
] as const;

export function clearSignInState() {
    AUTH_LOCALSTORAGE_KEYS.forEach((key) => {
        window.localStorage.removeItem(key);
    });
    portalUserService.clearCurrentUser();
}

export async function signInWithPassword(username: string, password: string): Promise<ILoginResponseViewModel> {
    // Step 1: POST to /auth/login (same as supplier portal login)
    // TODO: Extract to a shared AuthApi class (like UserApi, CompanyApi) when auth is
    // consolidated across portals. See also common/features/Login/Auth/AuthService.ts.
    // Uses api() to get interceptors (token expiration, caching, error normalization)
    const response = await api().post(
        '/auth/login',
        new URLSearchParams({
            grant_type: 'password',
            username,
            password,
        }).toString(),
        {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
    );

    const data = response.data;

    // Step 2: Store tokens in localStorage (same keys as supplier portal)
    // Mirrors supplier-portal/src/actions/userlogin.js lines 161-168
    window.localStorage.is_logged_in = 'true';
    window.localStorage.access_token = data.access_token;
    window.localStorage.refresh_token = data.refresh_token;
    window.localStorage.token_expires = data['.expires'];
    window.localStorage.expires_in = data.expires_in;
    window.localStorage.name = data.userName;
    window.localStorage.company_id = parseInt(data.companyId, 10);
    window.localStorage.id = data.userId;

    // Post-signup reads company ids from navigation state and authenticates
    // with the stored token, so we skip the full supplier-portal user
    // hydration (getUser / getPermissions / getCompany) it doesn't need.

    return data;
}

/** Same token writes as supplier-portal `refreshUserToken`. */
export async function refreshSignInToken() {
    const refreshToken = window.localStorage.getItem('refresh_token');
    if (!refreshToken) {
        throw new Error('Missing refresh token');
    }

    const { data } = await new backendServices.Apis.AuthApi().postLoginWithRefreshToken(refreshToken);

    window.localStorage.access_token = data.access_token;
    window.localStorage.refresh_token = data.refresh_token;
    window.localStorage.token_expires = data['.expires'];
}

export function useSilentSignIn() {
    const [state, setState] = useState<SignInState>({
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: null,
        userId: null,
    });

    const signIn = useCallback(async (username: string, password: string) => {
        setState({ isLoading: true, isSuccess: false, isError: false, error: null, userId: null });

        try {
            const data = await signInWithPassword(username, password);

            setState({ isLoading: false, isSuccess: true, isError: false, error: null, userId: data.userId });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Silent sign-in failed';
            console.error('[PostSignup] Silent sign-in failed:', message, err);
            setState({ isLoading: false, isSuccess: false, isError: true, error: message, userId: null });
        }
    }, []);

    return { ...state, signIn };
}
